import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, Coffee, Leaf, Package, Award, Star, Quote } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

const stagger = {
  container: {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.1 } },
  },
  item: {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  },
};

const features = [
  {
    icon: Leaf,
    title: "Özenle Seçilmiş Çekirdek",
    description: "En kaliteli Arabica çekirdekleri, uzman ekibimiz tarafından titizlikle seçilir.",
  },
  {
    icon: Coffee,
    title: "Geleneksel Öğütme",
    description: "Yüzyıllık tarihi yöntemlerle, taş değirmenlerde öğütülür.",
  },
  {
    icon: Award,
    title: "Yoğun Aroma",
    description: "Her fincanda derinlikli, zengin ve unutulmaz bir aroma deneyimi.",
  },
  {
    icon: Package,
    title: "Premium Paketleme",
    description: "Tazeliği koruyan özel ambalajlarla kapınıza kadar ulaşır.",
  },
];

const usageAreas = [
  { title: "Ev", description: "Evinizin sıcaklığında özel anlar için" },
  { title: "Ofis", description: "İş günlerinize enerji katın" },
  { title: "Hediye", description: "Sevdiklerinize özel bir armağan" },
  { title: "Cafe & Otel", description: "Profesyonel kullanım için" },
];

const testimonials = [
  {
    name: "Ayşe Yılmaz",
    role: "Kahve Tutkunu",
    content: "SümbülAğa ile tanıştığımdan beri başka kahve içemiyorum. O eşsiz aroma ve yumuşak içim gerçekten benzersiz.",
    rating: 5,
  },
  {
    name: "Mehmet Kaya",
    role: "Otel Müdürü",
    content: "Misafirlerimiz SümbülAğa'yı çok seviyor. Premium kalite ve tutarlı lezzet, işimizi kolaylaştırıyor.",
    rating: 5,
  },
  {
    name: "Zeynep Demir",
    role: "Gurme Blogger",
    content: "Türk kahvesi denince aklıma gelen ilk marka. Paketleme kalitesi ve sunum da ayrıca takdire şayan.",
    rating: 5,
  },
];

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products/featured`);
        setProducts(response.data);
      } catch (error) {
        console.error("Failed to fetch products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const handleAddToCart = (product) => {
    addToCart(product, 100, 1);
    toast.success(`${product.name} sepete eklendi`);
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section with Video Background */}
      <section
        className="relative min-h-screen flex items-center overflow-hidden"
        data-testid="hero-section"
      >
        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
            poster="https://images.unsplash.com/photo-1514432324607-a09d9b4aefdd?w=1920&q=80"
          >
            <source
              src="https://videos.pexels.com/video-files/7020812/7020812-uhd_2732_1440_25fps.mp4"
              type="video/mp4"
            />
          </video>
        </div>
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D3B2E]/90 via-[#0D3B2E]/70 to-[#0D3B2E]/50 z-10" />
        
        {/* Animated Gold Particles */}
        <div className="absolute inset-0 z-10 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-[#C5A059] rounded-full opacity-40"
              initial={{
                x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1920),
                y: -10,
              }}
              animate={{
                y: typeof window !== 'undefined' ? window.innerHeight + 10 : 1080,
                opacity: [0.2, 0.6, 0.2],
              }}
              transition={{
                duration: 8 + Math.random() * 4,
                repeat: Infinity,
                delay: Math.random() * 5,
                ease: "linear",
              }}
            />
          ))}
        </div>
        
        <div className="section-container relative z-20">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <motion.span 
              className="caption-text text-[#C5A059] mb-4 block"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              Premium Türk Kahvesi
            </motion.span>
            <h1 className="heading-xl text-[#F9F7F2] mb-6">
              <motion.span
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="block"
              >
                Her fincanda
              </motion.span>
              <motion.span
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="block text-gradient-gold"
              >
                zarafet
              </motion.span>
            </h1>
            <motion.p 
              className="body-text text-[#F9F7F2]/80 mb-10 max-w-xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              SümbülAğa Türk Kahvesi, geleneksel lezzeti modern bir yorumla sunar.
              Osmanlı zarafetini, seçkin kahve deneyimiyle birleştiren eşsiz bir
              marka.
            </motion.p>
            <motion.div 
              className="flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
            >
              <Link
                to="/urunler"
                className="btn-gold inline-flex items-center justify-center gap-2 group"
                data-testid="hero-cta-products"
              >
                <span>Ürünleri İncele</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/toptan-satis"
                className="bg-white/10 backdrop-blur-sm border border-[#F9F7F2]/50 text-[#F9F7F2] hover:bg-[#F9F7F2] hover:text-[#0D3B2E] px-8 py-4 uppercase tracking-widest text-xs font-medium inline-flex items-center justify-center gap-2 transition-all duration-300"
                data-testid="hero-cta-wholesale"
              >
                Toptan Teklif Al
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20"
        >
          <div className="w-6 h-10 border-2 border-[#F9F7F2]/30 rounded-full flex items-start justify-center p-2">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1 h-3 bg-[#C5A059] rounded-full"
            />
          </div>
        </motion.div>
        
        {/* Bottom Gradient Fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#F9F7F2] to-transparent z-20" />
      </section>

      {/* Brand Story Section */}
      <section className="section-spacing bg-[#F9F7F2]" data-testid="brand-story-section">
        <div className="section-container">
          <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="caption-text text-[#C5A059] mb-4 block">
                Hikâyemiz
              </span>
              <h2 className="heading-lg text-[#0D3B2E] mb-6">
                500 Yıllık Geleneğin
                <br />
                Modern Yorumu
              </h2>
              <div className="decorative-line mb-8" />
              <p className="body-text text-[#5D4037] mb-6">
                SümbülAğa, Osmanlı saray mutfağından ilham alan, geleneksel Türk
                kahvesi ustalığını çağdaş bir premium marka diliyle yeniden
                yorumlar.
              </p>
              <p className="body-text text-[#5D4037] mb-8">
                Her çekirdek özenle seçilir, her kavurma ustalıkla yapılır ve her
                paket, yüzyıllık bir geleneğin mirasını taşır.
              </p>
              <Link
                to="/hakkimizda"
                className="btn-outline inline-flex items-center gap-2"
                data-testid="brand-story-link"
              >
                Daha Fazlası
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1772744182940-56b3ce1b8bae?w=800&q=80"
                alt="Tarihi kahvehane"
                className="w-full aspect-[4/5] object-cover"
              />
              <div className="absolute -bottom-8 -left-8 bg-[#0D3B2E] text-[#F9F7F2] p-8 max-w-xs hidden md:block">
                <span className="text-4xl font-display text-[#C5A059]">500+</span>
                <p className="caption-text mt-2">Yıllık Kahve Geleneği</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="section-spacing bg-white" data-testid="featured-products-section">
        <div className="section-container">
          <motion.div {...fadeUp} className="text-center mb-16">
            <span className="caption-text text-[#C5A059] mb-4 block">
              Koleksiyonumuz
            </span>
            <h2 className="heading-lg text-[#0D3B2E]">Öne Çıkan Ürünler</h2>
          </motion.div>

          {loading ? (
            <div className="flex justify-center">
              <div className="spinner" />
            </div>
          ) : (
            <motion.div
              variants={stagger.container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            >
              {products.map((product) => (
                <motion.div
                  key={product.id}
                  variants={stagger.item}
                  className="card-product group"
                  data-testid={`product-card-${product.slug}`}
                >
                  <Link to={`/urunler/${product.slug}`} className="block">
                    <div className="relative overflow-hidden">
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="product-image transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-[#0D3B2E]/0 group-hover:bg-[#0D3B2E]/20 transition-all duration-500" />
                    </div>
                  </Link>
                  <div className="p-6">
                    <span className="caption-text text-[#C5A059] mb-2 block">
                      {product.roast_level}
                    </span>
                    <Link to={`/urunler/${product.slug}`}>
                      <h3 className="heading-md text-[#0D3B2E] mb-2 group-hover:text-[#C5A059] transition-colors">
                        {product.name}
                      </h3>
                    </Link>
                    <p className="body-text text-[#5D4037] text-sm mb-4 line-clamp-2">
                      {product.short_description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="font-display text-xl text-[#0D3B2E]">
                        ₺{product.price.toFixed(2)}
                      </span>
                      <button
                        onClick={() => handleAddToCart(product)}
                        className="btn-outline py-2 px-4 text-[10px]"
                        data-testid={`add-to-cart-${product.slug}`}
                      >
                        Sepete Ekle
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          <motion.div {...fadeUp} className="text-center mt-12">
            <Link
              to="/urunler"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="view-all-products"
            >
              Tüm Ürünleri Gör
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Why SümbülAğa */}
      <section className="section-spacing bg-[#0D3B2E] relative overflow-hidden" data-testid="why-section">
        <div className="absolute inset-0 opacity-5">
          <img
            src="https://images.unsplash.com/photo-1739475981256-1dc6f63d8e7c?w=1920&q=80"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="section-container relative z-10">
          <motion.div {...fadeUp} className="text-center mb-16">
            <span className="caption-text text-[#C5A059] mb-4 block">
              Farkımız
            </span>
            <h2 className="heading-lg text-[#F9F7F2]">Neden SümbülAğa?</h2>
          </motion.div>

          <motion.div
            variants={stagger.container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={stagger.item}
                className="card-feature border-[#F9F7F2]/20"
              >
                <feature.icon
                  className="w-8 h-8 text-[#C5A059] mb-4"
                  strokeWidth={1.5}
                />
                <h3 className="heading-md text-[#F9F7F2] text-xl mb-3">
                  {feature.title}
                </h3>
                <p className="body-text text-[#F9F7F2]/70 text-sm">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Usage Areas */}
      <section className="section-spacing bg-[#F9F7F2]" data-testid="usage-section">
        <div className="section-container">
          <motion.div {...fadeUp} className="text-center mb-16">
            <span className="caption-text text-[#C5A059] mb-4 block">
              Kullanım Alanları
            </span>
            <h2 className="heading-lg text-[#0D3B2E]">Her An, Her Yerde</h2>
          </motion.div>

          <motion.div
            variants={stagger.container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {usageAreas.map((area, index) => (
              <motion.div
                key={index}
                variants={stagger.item}
                className="text-center p-8 border border-[#E5E0D8] hover:border-[#C5A059] transition-colors"
              >
                <h3 className="heading-md text-[#0D3B2E] mb-2">{area.title}</h3>
                <p className="body-text text-[#5D4037] text-sm">
                  {area.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-spacing bg-white" data-testid="testimonials-section">
        <div className="section-container">
          <motion.div {...fadeUp} className="text-center mb-16">
            <span className="caption-text text-[#C5A059] mb-4 block">
              Müşteri Yorumları
            </span>
            <h2 className="heading-lg text-[#0D3B2E]">Onlar Ne Diyor?</h2>
          </motion.div>

          <motion.div
            variants={stagger.container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                variants={stagger.item}
                className="testimonial-card"
              >
                <Quote className="w-8 h-8 text-[#C5A059] mb-4" strokeWidth={1} />
                <p className="body-text text-[#5D4037] mb-6">
                  "{testimonial.content}"
                </p>
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-4 h-4 fill-[#C5A059] text-[#C5A059]"
                    />
                  ))}
                </div>
                <p className="font-display text-[#0D3B2E]">{testimonial.name}</p>
                <p className="caption-text text-[#5D4037]">{testimonial.role}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Instagram Section */}
      <section className="py-16 bg-[#F9F7F2]" data-testid="instagram-section">
        <div className="section-container mb-8 text-center">
          <span className="caption-text text-[#C5A059] mb-2 block">
            @sumbulaga
          </span>
          <h2 className="heading-md text-[#0D3B2E]">Instagram'da Biz</h2>
        </div>
        <div className="instagram-grid">
          {[
            "https://images.unsplash.com/photo-1544787219-7f47ccb76574?w=400&h=400&fit=crop",
            "https://images.unsplash.com/photo-1506619216599-9d16d0903dfd?w=400&h=400&fit=crop",
            "https://images.unsplash.com/photo-1559056199-641a0ac8b55e?w=400&h=400&fit=crop",
            "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=400&h=400&fit=crop",
            "https://images.unsplash.com/photo-1461023058943-07fcbe16d735?w=400&h=400&fit=crop",
            "https://images.unsplash.com/photo-1514432324607-a09d9b4aefdd?w=400&h=400&fit=crop",
          ].map((img, index) => (
            <a
              key={index}
              href="https://instagram.com/sumbulaga"
              target="_blank"
              rel="noopener noreferrer"
              className="instagram-item"
            >
              <img src={img} alt={`Instagram ${index + 1}`} />
            </a>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section
        className="section-spacing relative"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1768498855458-f6ce1068f02f?w=1920&q=80')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="final-cta-section"
      >
        <div className="absolute inset-0 bg-[#0D3B2E]/80" />
        <div className="section-container relative z-10 text-center">
          <motion.div {...fadeUp}>
            <h2 className="heading-lg text-[#F9F7F2] mb-6">
              Türk Kahvesinin Seçkin Haliyle
              <br />
              <span className="text-gradient-gold">Tanışın</span>
            </h2>
            <p className="body-text text-[#F9F7F2]/80 mb-10 max-w-xl mx-auto">
              SümbülAğa ile her yudum, yüzyıllık bir geleneğin modern yorumunu
              taşır.
            </p>
            <Link
              to="/urunler"
              className="btn-gold inline-flex items-center gap-2"
              data-testid="final-cta-button"
            >
              Şimdi Keşfet
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
